/**
 * Dependencies.
 */
import { AbstractControl, FormGroup } from '@prz-dp/services';
import { orderBy, uniqBy } from 'lodash-es';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import VueSignaturePad from 'vue-signature-pad';

import { ServicePointDeliverAlertModel } from '@/api/service-point/deliver-alert/deliver-alert.model';
import { servicePointDeliverAlertService } from '@/api/service-point/deliver-alert/deliver-alert.service';
import { FormDeliverModel } from '@/api/service-point/form-deliver/form-deliver.model';
import { ServicePointOrderDeliverModel } from '@/api/service-point/order-deliver/order-deliver.model';
import { servicePointOrderDeliverService } from '@/api/service-point/order-deliver/order-deliver.service';
import OrderSteps from '@/modules/service-point/components/order/order-steps/order-steps.component.vue';
import orderState from '@/modules/service-point/constants/order.constant';
import { servicePointStore } from '@/modules/service-point/store';

/**
 * Component.
 */
@Component({
  components: {
    'order-steps': OrderSteps,
    'signature-pad': VueSignaturePad
  },
  name: 'deliver-order-confirm-scene'
})
export default class extends Vue {
  /**
   * Property `formGroup`.
   */
  formGroup: FormGroup & {
    clientName?: AbstractControl & { value: string };
    deliverAlert?: AbstractControl & { value: string };
  } = null;

  /**
   * Property `isFormGroupValid`.
   */
  isFormGroupValid = false;

  /**
   * Property `isSavingDeliver`.
   */
  isSavingDeliver = false;

  /**
   * Property `deliverAlerts`.
   */
  deliverAlerts: ServicePointDeliverAlertModel[] = [];

  /**
   * Getter `clientName`.
   */
  get clientName() {
    return this.order?.delivery?.client?.name;
  }

  /**
   * Getter `isSignatureRequired`.
   */
  get isSignatureRequired() {
    return this.formGroup?.get('isSignatureRequired').value || false;
  }

  /**
   * Getter `order`.
   */
  get order() {
    const { orders = [] } = this;
    const { order } = servicePointStore;

    const orderInList = orders?.find(({ id }) => id === order?.id);

    const orderCode =
      order?.delivery?.orderCode || orderInList?.delivery?.orderCode;

    return {
      ...order,
      delivery: {
        ...order?.delivery,
        orderCode: orderCode || null
      }
    };
  }

  /**
   * Getter `orders`.
   */
  get orders() {
    return servicePointStore.orders;
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    return this.order?.delivery?.orderCode;
  }

  /**
   * Getter `packages`.
   */
  get packages() {
    let { packages = [] } = this.order;

    packages = uniqBy(packages, 'id');
    packages = orderBy(packages, ['status.code'], ['desc']);

    return packages;
  }

  /**
   * Getter `validFormGroup`.
   */
  get validFormGroup() {
    return this.isFormGroupValid;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    return this.order?.delivery?.trackingNumber;
  }

  /**
   * Getter `trackingNumberKey`.
   */
  get trackingNumberKey() {
    const trackingNumberKey = servicePointStore.deliverTrackingNumberKey;

    return trackingNumberKey;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `totalSteps`.
   */
  get totalSteps() {
    const { totalSteps = null } = this.$route.params || {};

    return Number(totalSteps) || 3;
  }

  /**
   * Lifecycle created.
   */
  async created() {
    const { trackingNumberKey } = this;

    if (trackingNumberKey) {
      await this.handleGetDeliverAlerts();

      const {
        delivery: { client: { name: clientName = null } = {} } = {}
      } = this.order;

      this.formGroup = this.$kit.forms.generateFormGroup(
        new FormDeliverModel({
          clientName
        })
      );
    } else {
      this.$router.push({
        name: 'ServicePointHome'
      });
    }
  }

  /**
   * Handle deliver alerts.
   */
  async handleGetDeliverAlerts() {
    try {
      this.deliverAlerts = await servicePointDeliverAlertService.get();
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetDeliverAlerts',
          scene: 'DeliverOrderConfirmScene'
        });
      }
    }
  }

  /**
   * Handle on end signature.
   */
  async handleOnEndSignature() {
    const data = (this.$refs['signature'] as any).saveSignature();
    const { data: signature, isEmpty } = data || {};

    if (!isEmpty) {
      this.formGroup.get('deliverSignature').setValue(signature);
      this.isFormGroupValid = await this.formGroup.validateForm();
    }
  }

  /**
   * Handle confirm delivery.
   */
  async handleConfirmDelivery() {
    const { trackingNumberKey } = this;
    const { packages = [] } = this.order;
    const orderPackages = packages.map(
      ({ deliveryPackage: { packageCode = null } = {} }) => ({
        packageCode,
        status: orderState.DELIVERED
      })
    );
    const reasonCode: string = this.formGroup.get('deliverAlert').value;
    const deliveredTo: string = this.formGroup.get('clientName').value;
    const signature: string = this.formGroup.get('deliverSignature').value;
    const isSignatureRequired: boolean = this.formGroup.get(
      'isSignatureRequired'
    ).value;

    const images: {
      encodedFileContent: string;
      mediaType: string;
      type: string;
    }[] = [];

    if (isSignatureRequired) {
      images.push({
        encodedFileContent: signature.split(',').pop(),
        mediaType: 'image/png;base64',
        type: 'SIGNATURE'
      });
    }

    const receiveData = new ServicePointOrderDeliverModel({
      deliveredTo,
      images,
      notes: null,
      orderPackages,
      reasonCode,
      satisfaction: 5
    });

    try {
      this.isSavingDeliver = true;

      const endpoint = servicePointOrderDeliverService.getModelEndpoint.replace(
        '{trackingNumberKey}',
        trackingNumberKey
      );

      await servicePointOrderDeliverService.POST(endpoint, receiveData);

      this.$router.push({
        name: 'ServicePointDeliverOrderStatus',
        params: {
          totalSteps: String(this.totalSteps)
        }
      });
    } catch (error) {
      this.$kit.notify.show(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG'),
        this.notifyErrorSettings
      );

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleConfirmDelivery',
          scene: 'DeliverOrderConfirmScene'
        });
      }
    } finally {
      this.isSavingDeliver = false;
    }
  }

  /**
   * Handle clear signature.
   */
  async handleClearSignature() {
    (this.$refs['signature'] as any).clearSignature();
    this.formGroup.get('deliverSignature').setValue(null);
    this.isFormGroupValid = await this.formGroup.validateForm();
  }

  /**
   * Handle go back.
   */
  handleGoback() {
    this.$router.back();
  }

  /**
   * Handle form group fields change.
   */
  @Watch('formGroup.deliverAlert.value')
  @Watch('formGroup.clientName.value')
  async handleFormGroupFieldsChange() {
    this.isFormGroupValid = await this.formGroup.validateForm();
  }
}
